import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Text } from 'view/atoms';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createAlumniUser } from 'services/users';
import { useAppDispatch } from 'redux/hooks';
import { showErrorAlert } from 'redux/utils';
import { capitalize } from 'utils';
import * as S from '../../ProvideAccessModal.styles';
import { schema } from './ProvideAccessForm.schema';

type Props = {
  setSharedEmail: (email: string) => void;
  afterSubmit: () => void;
};

type FormFields = { name: string; lastname: string; email: string };

export const ProvideAccessForm = ({ setSharedEmail, afterSubmit }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const {
    register,
    formState: { errors, isValid: isSubmitEnabled },
    handleSubmit,
    setValue,
    watch,
  } = useForm<FormFields>({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const onSubmit = async ({ name, lastname, email }: FormFields) => {
    try {
      await createAlumniUser({
        firstName: name,
        lastName: lastname,
        email,
        login: email.split('@')[0],
      });

      setSharedEmail(email);
      afterSubmit();
    } catch (err) {
      showErrorAlert(dispatch, err, 'Could not create an invitation');
    }
  };

  return (
    <S.ModalFormWrapper>
      <S.ContentWrapper>
        <S.HeaderWrapper>
          <Text kind='h2' data-testid='provide-access-modal-title'>{t('profile.accessModal.title')}</Text>
          <Text kind='body'>{t('profile.accessModal.description')}</Text>
        </S.HeaderWrapper>

        <Input
          type='text'
          placeholder={t('profile.accessModal.namePlaceholder')}
          {...register('name', {
            onChange: (e) => setValue('name', capitalize(e.target.value)),
            onBlur: (e) => setValue('name', capitalize(e.target.value)),
          })}
          error={errors.name?.message && t(errors.name.message)}
          hasCounter
          maxLength={30}
          value={watch('name')}
          data-testid='input-enter-name'
        />

        <Input
          type='text'
          placeholder={t('profile.accessModal.lastnamePlaceholder')}
          {...register('lastname', {
            onChange: (e) => setValue('lastname', capitalize(e.target.value)),
            onBlur: (e) => setValue('lastname', capitalize(e.target.value)),
          })}
          error={errors.lastname?.message && t(errors.lastname.message)}
          hasCounter
          maxLength={30}
          value={watch('lastname')}
          data-testid='input-enter-last-name'
        />

        <Input
          type='text'
          placeholder={t('profile.accessModal.emailPlaceholder')}
          {...register('email')}
          error={errors.email?.message && t(errors.email.message)}
          value={watch('email')}
          data-testid='input-enter-email'
        />
      </S.ContentWrapper>

      <Button
        kind='primary'
        disabled={!isSubmitEnabled}
        onClick={handleSubmit(onSubmit)}
        data-testid='button-provide-access'
      >
        {t('profile.accessModal.submit')}
      </Button>
    </S.ModalFormWrapper>
  );
};
